import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, AlertTriangle, Activity, Users, MessageSquare, Zap, Wallet, Flame, Play, RefreshCw, Hourglass, TrendingUp, TrendingDown, Minus, Send, Check, UserCheck, Plus } from "lucide-react";
import { api, fmt, errMsg } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { StatusChip } from "@/components/ui-bits";

const when = (iso) => { const d = new Date(iso); return d.toDateString() === new Date().toDateString() ? fmt(iso, { hour: "2-digit", minute: "2-digit" }) : fmt(iso, { day: "numeric", month: "short" }); };
const money = (v, cur) => new Intl.NumberFormat(undefined, { style: "currency", currency: (cur || "usd").toUpperCase(), maximumFractionDigits: 0 }).format(v || 0);
const dayKey = (iso) => { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

const Trend = ({ dir, label, testId }) => {
  const Icon = dir > 0 ? TrendingUp : dir < 0 ? TrendingDown : Minus;
  return <span className={`trend ${dir > 0 ? "up" : dir < 0 ? "down" : "flat"}`} data-testid={testId}><Icon size={13} strokeWidth={2.6} />{label}</span>;
};

const Sparkline = ({ data }) => {
  const w = 110, h = 30, max = Math.max(...data, 1);
  const pts = data.map((v, i) => [i * (w / (data.length - 1)), h - 3 - (v / max) * (h - 6)]);
  const line = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  return (
    <svg className="spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true" data-testid="kpi-revenue-spark">
      <polygon points={`0,${h} ${line} ${w},${h}`} className="fill" /><polyline points={line} className="line" /><circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2.6" className="dot" />
    </svg>
  );
};

const Donut = ({ rate, completed, booked }) => {
  const r = 52, c = 2 * Math.PI * r;
  return (
    <div className="donut" data-testid="completion-donut">
      <svg viewBox="0 0 130 130">
        <circle cx="65" cy="65" r={r} className="track" />
        <circle cx="65" cy="65" r={r} className="arc" style={{ strokeDasharray: c, strokeDashoffset: c * (1 - rate / 100) }} />
      </svg>
      <div className="donut-mid"><b className="num" data-testid="completion-rate">{rate}%</b><small>{completed}/{booked} done</small></div>
    </div>
  );
};

const KpiCard = ({ i, icon: Icon, label, value, trend, hot, testId, to, children }) => {
  const Tag = to ? Link : "div";
  return (
    <Tag to={to} className={`kpi glass ${hot ? "hot" : ""} ${children ? "revenue" : ""}`} style={{ "--i": i }} data-testid={`${testId}-card`}>
      <div className="kpi-top"><span className="ic"><Icon size={18} strokeWidth={2.2} /></span>{trend}</div>
      <div className="l">{label}</div>
      <div className="v num" data-testid={testId}>{value}</div>
      {children}
    </Tag>
  );
};

const Panel = ({ icon: Icon, title, aside, area, testId, children }) => (
  <section className="panel tight glass" style={{ gridArea: area }} data-testid={testId}>
    <div className="ph"><h3><Icon size={15} /> {title}</h3>{aside}</div>
    <div className="scroll">{children}</div>
  </section>
);

export default function Overview() {
  const [d, setD] = useState(null);
  const [openJob, setOpenJob] = useState(null);
  const [nudging, setNudging] = useState(null);
  const [nudged, setNudged] = useState({});
  const nav = useNavigate();
  const load = useCallback(() => api.get("/admin/overview").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="center"><span className="spin" /></div>;
  const { revenue: rev, trends: t, completion: cp } = d;
  const revDelta = rev.prevMonth > 0 ? Math.round(((rev.month - rev.prevMonth) / rev.prevMonth) * 100) : null;
  const renew = async (c) => {
    try { await api.post(`/admin/overview/renew/${c.clientId}`); toast.success(`${c.programName} renewed for ${c.name}`); load(); } catch (ex) { toast.error(errMsg(ex)); }
  };
  const nudge = async (s) => {
    setNudging(s.clientId);
    try { const r = await api.post(`/admin/streaks/${s.clientId}/nudge`); toast.success(`Nudge sent to ${s.name} via ${r.data.channel.toLowerCase()}`); setNudged((n) => ({ ...n, [s.clientId]: true })); } catch (ex) { toast.error(errMsg(ex)); if (ex?.response?.status === 409) setNudged((n) => ({ ...n, [s.clientId]: true })); }
    setNudging(null);
  };
  return (
    <div className="dash fade" data-testid="admin-overview">
      <div className="dash-kpis">
        <KpiCard i={0} icon={CalendarDays} label="Sessions this week" value={d.sessionsThisWeek} testId="kpi-sessions-week"
          trend={<Trend dir={t.sessions} label={`${t.sessions > 0 ? "+" : ""}${t.sessions} vs last wk`} testId="trend-sessions" />} />
        <KpiCard i={1} icon={AlertTriangle} label="Needs attention" value={d.needsAttention} hot={d.needsAttention > 0} testId="kpi-needs-attention"
          trend={<Trend dir={t.urgent > 0 ? -1 : 0} label={t.urgent > 0 ? `${t.urgent} within 48h` : "none urgent"} testId="trend-attention" />} />
        <KpiCard i={2} icon={Activity} label="Weekly adherence" value={`${d.adherence}%`} testId="kpi-adherence"
          trend={<Trend dir={t.adherence} label={`${t.adherence > 0 ? "+" : ""}${t.adherence} pts`} testId="trend-adherence" />} />
        <KpiCard i={3} icon={Users} label="Active clients" value={d.activeClients} testId="kpi-active-clients"
          trend={<Trend dir={t.newClients > 0 ? 1 : 0} label={`+${t.newClients} this month`} testId="trend-clients" />} />
        <KpiCard i={4} icon={Wallet} label="Monthly revenue" value={money(rev.month, rev.currency)} testId="kpi-revenue" to="/admin/payments"
          trend={<Trend dir={revDelta === null ? (rev.month > 0 ? 1 : 0) : Math.sign(revDelta)} label={revDelta === null ? (rev.month > 0 ? "first month" : "no payments yet") : `${revDelta > 0 ? "+" : ""}${revDelta}% MoM`} testId="trend-revenue" />}>
          <Sparkline data={rev.trend} />
        </KpiCard>
      </div>

      <div className="dash-grid">
        <Panel icon={CalendarDays} title="Upcoming sessions" area="upcoming" testId="dash-upcoming" aside={<Link to="/admin/schedule" className="ss" data-testid="dash-schedule-link">Full schedule →</Link>}>
          {d.upcoming.length === 0 && <div className="ss empty" data-testid="dash-upcoming-empty">Nothing upcoming.</div>}
          {d.upcoming.map((s) => (
            <div className="row sess" key={s.id} data-testid={`upcoming-session-${s.id}`}>
              <Avatar client={{ id: s.clientId, photoKey: s.clientPhotoKey, name: s.clientName }} size={30} radius={8} />
              <div className="sess-info"><b className="nm">{s.clientName}</b><small className="ss">{s.title}{s.changeNote ? ` · wants: ${s.changeNote}` : ""}</small></div>
              <span className="num tm">{fmt(s.startAt, { weekday: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
              {s.coachName && d.team.members.length > 1 && <span className="coach-chip" title={`Coach: ${s.coachName}`} data-testid={`session-coach-${s.id}`}>{s.coachName.split(" ").map((w) => w[0]).join("").slice(0, 2)}</span>}
              <StatusChip status={s.status} />
              <button type="button" className="cta" onClick={() => nav(`/admin/schedule?date=${dayKey(s.startAt)}&session=${s.id}`)} data-testid={`start-session-${s.id}`}><Play size={12} strokeWidth={3} fill="currentColor" /> Start</button>
            </div>
          ))}
        </Panel>

        <Panel icon={Flame} title="Adherence streaks" area="streaks" testId="dash-streaks" aside={<span className="ss">{d.streaks.filter((s) => s.atRisk).length} at risk</span>}>
          {d.streaks.length === 0 && <div className="ss empty" data-testid="dash-streaks-empty">No completed sessions yet — streaks start after the first week.</div>}
          {d.streaks.map((s, i) => (
            <div className={`row streak ${s.atRisk ? "risk" : ""}`} key={s.clientId} data-testid={`streak-${s.clientId}`}>
              <span className="rank num">{i + 1}</span>
              <Avatar client={{ id: s.clientId, photoKey: s.photoKey, name: s.name }} size={28} radius={7} />
              <Link to="/admin/clients" className="nm" data-testid={`streak-name-${s.clientId}`}>{s.name}</Link>
              {s.atRisk && <span className="risk-tag" data-testid={`streak-risk-${s.clientId}`}>{s.broken ? "Streak broken" : "At risk"}</span>}
              {s.atRisk && (
                <button type="button" className={`cta ${nudged[s.clientId] ? "done" : ""}`} disabled={nudging === s.clientId || nudged[s.clientId]} onClick={() => nudge(s)} data-testid={`nudge-${s.clientId}`}>
                  {nudged[s.clientId] ? <><Check size={12} strokeWidth={3} /> Nudged</> : <><Send size={12} strokeWidth={3} /> {nudging === s.clientId ? "Sending…" : "Nudge"}</>}
                </button>
              )}
              <span className={`flame ${s.streak >= 4 ? "hot" : s.streak >= 1 ? "warm" : "cold"}`}><Flame size={15} strokeWidth={2.4} fill={s.streak ? "currentColor" : "none"} /><b className="num">{s.streak}</b><small>wk</small></span>
            </div>
          ))}
        </Panel>

        <Panel icon={Hourglass} title="Expiring programs" area="expiring" testId="dash-expiring" aside={<Link to="/admin/programs" className="ss" data-testid="dash-programs-link">Programs →</Link>}>
          {d.expiringPrograms.length === 0 && <div className="ss empty" data-testid="dash-expiring-empty">No programs ending in the next 7 days.</div>}
          {d.expiringPrograms.map((c) => (
            <div className={`row prog ${c.daysLeft <= 2 ? "urgent" : ""}`} key={c.clientId} data-testid={`expiring-${c.clientId}`}>
              <Avatar client={{ id: c.clientId, photoKey: c.photoKey, name: c.name }} size={30} radius={8} />
              <div className="sess-info"><b className="nm">{c.name}</b><small className="ss">{c.programName}</small></div>
              <span className={`days num ${c.daysLeft <= 2 ? "urgent" : ""}`} data-testid={`expiring-days-${c.clientId}`}>{c.daysLeft < 0 ? `${-c.daysLeft}d overdue` : c.daysLeft === 0 ? "ends today" : `${c.daysLeft}d left`}</span>
              <button type="button" className="cta" onClick={() => renew(c)} data-testid={`renew-${c.clientId}`}><RefreshCw size={12} strokeWidth={3} /> Renew</button>
            </div>
          ))}
          {d.expiringPrograms.length > 0 && <div className="ss ontrack" data-testid="dash-expiring-ontrack">All other active programs are on track.</div>}
        </Panel>

        <Panel icon={MessageSquare} title="Recent messages" area="messages" testId="dash-messages" aside={<Link to="/admin/messages" className="ss" data-testid="dash-messages-link">{d.totalUnread ? `${d.totalUnread} unread · ` : ""}Open inbox →</Link>}>
          {d.recentMessages.length === 0 && <div className="ss empty" data-testid="dash-messages-empty">No conversations yet.</div>}
          {d.recentMessages.map((m) => (
            <Link to="/admin/messages" className={`row msg ${m.unread ? "unread" : ""}`} key={m.id} data-testid={`recent-msg-${m.id}`}>
              <Avatar client={{ id: m.clientId, photoKey: m.photoKey, name: m.name }} size={30} radius={8} />
              <div className="sess-info"><b className="nm">{m.unread && <i className="pulse-dot sm" />}{m.name}</b><small className="ss prev">{m.direction === "INBOUND" ? "" : "You: "}{m.body}</small></div>
              <span className="ss tm">{when(m.at)}</span>
            </Link>
          ))}
        </Panel>

        <Panel icon={Activity} title="Session completion" area="completion" testId="dash-completion" aside={<span className="ss">this week</span>}>
          <div className="completion">
            <Donut rate={cp.rate} completed={cp.completed} booked={cp.booked} />
            <div className="legend">
              <div data-testid="completion-completed"><i className="sw done" /><span>Completed</span><b className="num">{cp.completed}</b></div>
              <div data-testid="completion-upcoming"><i className="sw up" /><span>Still to run</span><b className="num">{cp.upcoming}</b></div>
              <div data-testid="completion-missed"><i className="sw miss" /><span>No-shows</span><b className="num">{cp.missed}</b></div>
              <div data-testid="completion-booked"><i className="sw all" /><span>Booked</span><b className="num">{cp.booked}</b></div>
            </div>
          </div>
        </Panel>

        <Panel icon={Zap} title="Automations" area="automations" testId="dash-automations" aside={<Link to="/admin/automations" className="ss" data-testid="overview-automations-link">Run manually →</Link>}>
          {d.automations.map((a) => (
            <div key={a.job} data-testid={`automation-${a.job}`}>
              <button type="button" className="row auto" onClick={() => setOpenJob(openJob === a.job ? null : a.job)} data-testid={`automation-toggle-${a.job}`}>
                <b className="nm"><i className={`pulse-dot ${a.lastRun ? "" : "off"}`} data-testid={`automation-pulse-${a.job}`} />{a.label}</b>
                <span className={`ss ${a.lastRun ? "" : "never"}`}>{a.lastRun ? `last run ${fmt(a.lastRun.ranAt, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : "never run"}</span>
              </button>
              {openJob === a.job && <div className="ss auto-desc" data-testid={`automation-desc-${a.job}`}>{a.schedule} · {a.desc}</div>}
            </div>
          ))}
        </Panel>

        <Panel icon={UserCheck} title="Staff on today" area="staff" testId="dash-staff" aside={<Link to="/admin/settings#profile" className="ss" data-testid="dash-team-link">{d.team.pendingInvites ? `${d.team.pendingInvites} invite${d.team.pendingInvites > 1 ? "s" : ""} pending · ` : ""}Manage team →</Link>}>
          <div className="staff-row">
            {d.team.members.map((m) => (
              <div className={`staff ${m.onToday ? "on" : ""}`} key={m.id} data-testid={`staff-${m.id}`}>
                <Avatar client={{ id: m.id, name: m.name }} size={40} radius={12} />
                <div className="staff-info">
                  <b>{m.name}</b>
                  <small>{m.isOwner ? "Owner · Head coach" : "Coach"}</small>
                  <span className={`staff-status ${m.onToday ? "on" : ""}`}>{m.onToday ? `On today · ${m.sessionsToday} session${m.sessionsToday === 1 ? "" : "s"}` : "No sessions today"}</span>
                  {m.clientsToday?.length > 0 && <span className="staff-clients" data-testid={`staff-clients-${m.id}`}>Training {m.clientsToday.join(", ")}{m.sessionsToday > m.clientsToday.length ? " +" : ""}</span>}
                </div>
              </div>
            ))}
            <Link to="/admin/settings#profile" className="staff add" data-testid="staff-invite"><span className="plus"><Plus size={18} strokeWidth={2.6} /></span><div className="staff-info"><b>Invite a coach</b><small>Add staff and split your schedule</small></div></Link>
          </div>
        </Panel>
      </div>
    </div>
  );
}
